import { SelectionModel } from '@angular/cdk/collections';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import { AppSelectOption } from '../../../../../../_base-shared/contracts/common.interface';
import { LaravelResourceResponse } from '../../../../../../_base-shared/contracts/laravel-response.interface';
import { DistributionBatch } from '../../../../../../_base-shared/models/Distribution/DistributionBatch';
import { DistributionProvider } from '../../../../../../_base-shared/models/Distribution/DistributionProvider';
import { User } from '../../../../../../_base-shared/models/User/User';
import {
  DistributionAmountCalculatorService
} from '../distribution-amount-calculator.service';
import { MainGlobalEventService } from '../../../_shared/services/main-global-event.service';
import { CaseService } from '../../case/case.service';
import { DistributionBatchService } from '../distribution-batch.service';
import { DistributionProviderService } from '../distribution-provider.service';
import { DistributionService } from '../distribution.service';
import { CaseListFilter } from '../../../../../../_base-shared/models/Case/CaseListFilter';
import { MatCheckboxChange } from '@angular/material/checkbox';
import Swal from 'sweetalert2';
import { MainBaseApiService } from '../../../_shared/services/main-base-api.service';
import { CaseDistribution } from 'projects/_base-shared/models/Distribution/CaseDistribution';

import { DistributionBatchListFiltersComponent } from './distribution-batch-list-filters/distribution-batch-list-filters.component'
// change to distributionListFilter?
import { PaymentListFilter } from '../../../../../../_base-shared/models/Payment/PaymentListFilter';

@Component({
  standalone: false,
  selector:    'app-distribution-batch-list',
  templateUrl: './distribution-batch-list.component.html',
  styleUrls:   ['./distribution-batch-list-component.scss']
  /*styles:      [
    `
      mat-table {
        display: table;
        width: 100%;
      }

      mat-progress-bar {
        height: 16px;
      }
    `,
  ],*/
})
export class DistributionBatchListComponent extends DistributionAmountCalculatorService implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  public authUser: User;
  public componentType: 'distribution' | 'admin';
  public filtersReady = false;
  public requestFilters: any;
  public form: UntypedFormGroup;
  public batches: MatTableDataSource<DistributionBatch> = new MatTableDataSource<DistributionBatch>([]);
  public distributionProviders: Array<DistributionProvider> = [];
  public isLoading = 0;
  public serverResponse: LaravelResourceResponse;
  public displayedColumns: Array<string>;
  public selection = new SelectionModel(true, []);
  public defaultPaginatorConfig: { pageIndex: number, pageSize: number, length: number };
  public paginatorConfig: { pageIndex: number, pageSize: number, length: number };
  public defaultSort: { direction: 'asc' | 'desc', active: 'name' };
  public totalResults: number;
  public totalPages: number;
  public pageSelected: boolean;
  public isAssigningBatch: boolean;

  // Filters
  public batchStatusOptions: Array<AppSelectOption> = [];
  public caseListFilter: CaseListFilter; ////? maybe replace

  private subscriptions: Array<Subscription> = [];

  constructor(private route: ActivatedRoute,
              private fb: UntypedFormBuilder,
              private dialog: MatDialog,
              private translate: TranslateService,
              private toastr: ToastrService,
              private globalEventService: MainGlobalEventService,
              private caseService: CaseService,
              private distributionService: DistributionService,
              private distributionBatchService: DistributionBatchService,
              private distributionProviderService: DistributionProviderService) {
    super();
  }

  ngOnInit(): void {
    this.defaultPaginatorConfig = { pageIndex: 0, pageSize: 20, length: 1 };
    this.defaultSort = { direction: 'asc', active: 'name' };
    this.paginatorConfig = this.defaultPaginatorConfig;
    this.requestFilters = this.getDefaultFilters();
    this.buildFilterOptions();
    this.fetchDistributionProviders();
    this.buildForm();

    this.globalEventService.authUser$.subscribe(user => {
      if (user) {
        this.authUser = user;
        this.componentType = this.authUser.role.slug === 'distribution-provider' ? 'distribution' : 'admin';
        this.displayedColumns = this.getTableColumns(this.componentType);
        this.fetchBatches();
      }
    });
  }

  ngAfterViewInit(): void {
    this.batches.sort = this.sort;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  private resetPagination(): void {
    this.paginatorConfig = this.defaultPaginatorConfig;
    this.requestFilters.per_page = this.paginatorConfig.pageSize;
    this.requestFilters.page = this.paginatorConfig.pageIndex;
  }

  private resetSort(): void {
    this.requestFilters.sort_by = this.defaultSort.active;
    this.requestFilters.order = this.defaultSort.direction;
  }

  private clearSelection() {
    this.selection.clear();
    //delete this.requestFilters.cases;
    this.pageSelected = false;
  }

  private fetchBatches(): void {
    const phaseTwoStatusIds = [414, 415];
  
    this.clearSelection();
    this.batches = new MatTableDataSource<any>([]);
    this.isLoading++;
  
    const fetchSubscriptions = this.distributionBatchService
      .index(this.requestFilters, [
        'distribution_provider',
        'case_distributions',
        'case_distributions.case',
        'case_distributions.case.unsecured_creditors',
        'case_distributions.case.creditors',
        'case_distributions.case.debt_payment_plan',
        'case_distributions.case.terms',
        'case_distributions.case.expense',
        'case_distributions.case.distribution_case_terms'
      ])
      .pipe(finalize(() => this.isLoading--))
      .subscribe(
        result => {
          this.clearSelection();
          const unifiedData = this.processAllBatches(result.data);
          this.updateBatchData(unifiedData, result.meta);
        },
        err => console.error(err)
      );
  
    this.subscriptions.push(fetchSubscriptions);
  }

  private processAllBatches(batches: any[]): any[] {
    let totalTerms = 0;
    let totalUniqueCaseIds = new Set<number>();
    let sumTotalUniqueCaseIds = 0;
    let totalBatchContractAmount = 0;
    let unifyeBatch = null;

    const processedBatches = batches.map(batch => {
        if (batch.name === 'UNIFYE') {
            // Save UNIFYE batch for later processing
            unifyeBatch = batch;
            return null;
        }

        const processedBatch = this.processBatch(batch);
        if (processedBatch.uniqueCaseIdCount > 0){
          totalTerms += processedBatch.terms * processedBatch.uniqueCaseIdCount;

          // Add unique case IDs to the total set
          batch.case_distributions.forEach(caseDistribution => {
              totalUniqueCaseIds.add(caseDistribution.case.id);
          });

          sumTotalUniqueCaseIds += Number(totalUniqueCaseIds.size);
          totalBatchContractAmount += Number(processedBatch.total_batch_contract_amount);
          //terms *months
          //totalBatchContractAmount += Number(totalTerms);
        }

        return processedBatch;
    }).filter(batch => batch !== null);  // Filter out null (UNIFYE)

    const unifiedTerms = totalUniqueCaseIds.size > 0 ? totalTerms / totalUniqueCaseIds.size : 0;

    if (unifyeBatch) {
        const processedUnifyeBatch = this.processBatch(unifyeBatch);

        const c_total_batch_contract_amount = processedUnifyeBatch.case_distributions.reduce((accumulator, current) => {
          const distributionFee = current.case.expense.distribution_fee;
          const phaseTwoDuration = current.case.debt_payment_plan.phase_two_duration;
          return accumulator + (distributionFee * phaseTwoDuration);
        }, 0);

        /*processedBatches.push({
            ...processedUnifyeBatch,
            terms: unifiedTerms / sumTotalUniqueCaseIds,
            total_batch_contract_amount: c_total_batch_contract_amount
        });*/

        processedBatches.unshift({
          ...processedUnifyeBatch,
          terms: unifiedTerms / sumTotalUniqueCaseIds,
          total_batch_contract_amount: c_total_batch_contract_amount
        });

    }

    return processedBatches;
  }
  
  private processBatch(batch: any): any {
    let c_amount_collected = 0;
    let c_default_rate = 0;
    let c_distributed = 0;
    let c_on_hold = 0;
    let case_offer_to_creditor = 0;
  
    const { terms, uniqueCaseIdCount } = this.calculateTermsAndUniqueCases(batch);
    const processedCaseIds = new Set();
  
    batch.case_distributions.forEach(caseDistribution => {
      if (!processedCaseIds.has(caseDistribution.case.id)) {
        processedCaseIds.add(caseDistribution.case.id);
  
        const caseOfferData = this.calculateCaseOfferData(caseDistribution, batch);
        case_offer_to_creditor += caseOfferData.case_offer_to_creditor;
  
        const termData = this.calculateTermData(caseDistribution, batch);
        c_default_rate = termData.default_rate;
        c_distributed += termData.amount_distributed;
        c_amount_collected += caseOfferData.case_offer_to_creditor * termData.number_of_payments;
        c_on_hold += termData.amount_holded;
      }
    });
  
    return {
      ...batch,
      terms: terms / uniqueCaseIdCount.size,
      c_amount_collected,
      c_default_rate,
      uniqueCaseIdCount: uniqueCaseIdCount.size,
      c_distributed,
      c_on_hold
    };
  }
  
  private calculateTermsAndUniqueCases(batch: any): { terms: number; uniqueCaseIdCount: Set<number> } {
    const result = batch.case_distributions.reduce((acc, obj) => {
      const caseId = obj.case.id;
      if (!acc.uniqueCaseIds.has(caseId)) {
        acc.uniqueCaseIds.add(caseId);
        const unsecuredCreditor = obj.case.unsecured_creditors.find(item => item.name === batch.name);
        if (unsecuredCreditor) {
          acc.terms += unsecuredCreditor.pivot.terms;
        }
      }
      return acc;
    }, { uniqueCaseIds: new Set(), terms: 0 });
  
    return { terms: result.terms, uniqueCaseIdCount: result.uniqueCaseIds };
  }
  
  private calculateCaseOfferData(caseDistribution: any, batch: any): { case_offer_to_creditor: number } {
    const case_creditors = batch.name === 'UNIFYE'
      ? caseDistribution.case.creditors.filter(item => item.name === batch.name)
      : caseDistribution.case.unsecured_creditors.filter(item => item.name === batch.name);
  
    const totalOfferToCreditor = case_creditors.reduce((sum, creditor) => sum + creditor.pivot.offer_to_creditor, 0);
  
    return {
      case_offer_to_creditor: totalOfferToCreditor || 0
    };
  }
  
  private calculateTermData(caseDistribution: any, batch: any): { number_of_payments: number; amount_distributed: number; amount_paid: number; amount_holded: number; default_rate: number } {
    let number_of_payments = 0;
    let total_amount_paid = 0;
    let total_amount_requested = 0;
    let amount_distributed = 0; 
    let amount_paid = 0; //delete if never used
    let amount_holded = 0;
  
    caseDistribution.case.distribution_case_terms.forEach(term => {
      if (this.isValidPhaseTwoTerm(term, batch.id)) {
        number_of_payments++;
        if (this.isTermWithin18Months(term)) {
          total_amount_paid += term.amount_paid;
          total_amount_requested += term.amount;
        }
        if (term.hold_distribution && (term.amount_paid == term.amount)){
          amount_holded += term.amount_to_distribute;
        }
        //amount_paid += term.amount_to_distribute;
      }
  
      if (this.isDistributedPhaseTwoTerm(term, batch.id)) {
        amount_distributed += term.amount_distributed;
      }
    });
  
    const default_rate = total_amount_requested > 0
      ? (total_amount_requested - total_amount_paid) / total_amount_requested
      : 0;
  
    return {
      number_of_payments,
      amount_distributed,
      default_rate,
      amount_paid, // delete?
      amount_holded
    };
  }
  
  private isValidPhaseTwoTerm(term: any, batchId: number): boolean {
    return term.type === "phase_two" && term.amount_paid > 0 && term.amount_paid === term.amount && term.distribution_batch_id === batchId;
  }
  
  private isDistributedPhaseTwoTerm(term: any, batchId: number): boolean {
    return term.type === "phase_two" && term.amount_distributed > 0 && term.distribution_batch_id === batchId;
  }
  
  private isTermWithin18Months(term: any): boolean {
    const today = new Date();
    today.setMonth(today.getMonth() + 18);
    return new Date(term.term_date) <= today;
  }
  
  private updateBatchData(dataWithTerms: any[], meta: any): void {
    this.batches = new MatTableDataSource<any>(dataWithTerms);
    //this.batches.sort = this.sort;
    this.paginatorConfig.length = meta.total;
    this.totalResults = meta.total;
    this.totalPages = meta.last_page;
  }

  //end-refactor

  private getTableColumns(type: 'distribution' | 'admin'): Array<string> {
    return [
      //'select',
      'name',
      'cases_count',
      //'status_updated_at',
      'total_batch_contract_amount',
      'total_monthly_fee_amount',
      'amount_collected', 
      'balance_outstanding', 
      'amount_distributed', 
      'current_distribution', 
      'on-account',
      'default_rate', 
      'total_fees_retained', 
      'average_term',
      //'progress_bar',
      'actions',
    ];
  }

  public getPercent(paid, amount) {
    if (( paid / amount ) * 100) {
      return ( ( paid / amount ) * 100 ).toFixed(2);
    } else {
      return 0;
    }
  }

  private getDefaultFilters() {
    return {
      select_all:                0,
      statuses:                  null,
      distribution_provider_ids: null,
      date_filter: "terms",
      sort_by: "name",
      order: "asc",
    };
  }

  public paginatorChange($event: PageEvent): void {
    this.paginatorConfig.pageIndex = $event.pageIndex;
    this.paginatorConfig.pageSize = $event.pageSize;
    this.paginatorConfig.length = $event.length;

    this.requestFilters.page = this.paginatorConfig.pageIndex + 1;
    this.requestFilters.per_page = this.paginatorConfig.pageSize;
    this.fetchBatches();
  }

  public sortData(sort) {
    this.requestFilters.sort_by = sort.active ? sort.active : this.defaultSort.active;
    this.requestFilters.order = sort.direction ? sort.direction : this.defaultSort.direction;

    this.fetchBatches();
  }

  // On filter change
  public handleFilters(): void {
    // this.requestFilters = $event;
    this.resetPagination();
    this.resetSort();
    this.fetchBatches();
  }

  private buildForm() {
    this.form = this.fb.group({
      statuses:                  [null],
      distribution_provider_ids: [null],
    });
    this.subscribeToFilterChanges();
  }

  private buildFilterOptions() {
    this.batchStatusOptions = [
      { value: 'pending', label: 'Pending' },
      { value: 'submitted', label: 'Submitted' },
      { value: 'funded', label: 'Funded' },
      { value: 'rejected', label: 'Rejected' },
    ];
  }

  private fetchDistributionProviders() {
    this.isLoading++;
    this.distributionProviderService.index({ select_all: 1 }).pipe(finalize(() => this.isLoading--))
      .subscribe(result => this.distributionProviders = result.data,
        () => this.toastr.error(this.translate.instant('SHARED.went-wrong')),
      );
  }

  private subscribeToFilterChanges() {
    this.subscriptions.push(
      this.form.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
      ).subscribe(formValue => {
        this.requestFilters = { ...this.requestFilters, ...formValue };
        this.handleFilters();
      }),
    );
  }

  public changeComponentType(type: 'distribution' | 'admin') {
    this.componentType = type;
    this.displayedColumns = this.getTableColumns(type);
  }

  // Select All
  public toggleRow(event: MatCheckboxChange, batches: DistributionBatch) {
    this.selection.toggle(batches);
    if (!event.checked) {
      this.pageSelected = false;
      this.patchSelectAllFilter(0);
    }
  }

  public allRowsSelected() {
    return this.selection.selected.length === this.batches.data.length;
  }

  public togglePageSelect($event) {
    this.pageSelected = $event.checked;
    if (!this.pageSelected) {
      this.patchSelectAllFilter(0);
    }
    if (this.allRowsSelected()) {
      this.batches.data.forEach(wallet => this.selection.deselect(wallet));
    } else {
      this.batches.data.forEach(wallet => this.selection.select(wallet));
    }
  }

  public globalSelectAll($event) {
    $event.preventDefault();
    this.patchSelectAllFilter(1);
  }

  public globalClearSelectAll($event) {
    $event.preventDefault();
    this.clearSelection();
  }

  // End Select All

  private patchSelectAllFilter(state: boolean | 0 | 1) {
    if (this.caseListFilter) {
      // this.caseListFiltersComponent.patchFilter('select_all', state, {emitEvent: false, onlySelf: true});
      this.caseListFilter.select_all = state;
    } else {
      console.log('Filter component not ready for form pControl patching');
    }
  }

  public confirmDistributeWallet() {
    Swal.fire({
      title:              this.translate.instant('SHARED.warning'),
      text:               "Are you sure that you want to distribute the wallets?",
      icon:               'warning',
      showCancelButton:   true,
      cancelButtonText:   this.translate.instant('SHARED.no'),
      confirmButtonText:  this.translate.instant('SHARED.yes'),
      confirmButtonColor: '#886ab5',
    }).then(res => {
      if (res.isConfirmed) {
        this.distributeWallet(); //this.caseListFilter
      } else {
        this.clearSelection();
      }
    });
  }

  public distributeWallet() { //caseListFilter: CaseListFilter
  
    let wallet_filter = [];
    this.selection.selected.forEach(selectedCase => wallet_filter.push(selectedCase.id));
    
    this.isAssigningBatch = true;
    this.subscriptions.push(
      this.distributionService.distributeWallet({ filters: wallet_filter })
        .pipe(finalize(() => this.isAssigningBatch = false))
        .subscribe(
          result => {

            if (result.message === 'non-existant-wallet'){
              this.toastr.error(
                'Wallet: ' + result.data + ' not available'
              );
            }else{
            
              this.toastr.success(
                this.translate.instant('DISTRIBUTION.cases.list.actions.assign_batch.result.success'));
              //if (caseListFilter.select_all) {
                //this.resetTable();
                //this.fetchCases();
              //  return;
              //}
              /*const localCases = this.batches.data;
              result.data.forEach(clientCase => {
                const localCaseIndex = localCases.findIndex(sCase => sCase.id === clientCase.id);
                if (localCaseIndex >= 0) {
                  localCases[localCaseIndex].distribution = clientCase.distribution;
                } else {
                  localCases.unshift(clientCase);
                }
              });
              this.cases = new MatTableDataSource<Case>(localCases);*/
              this.clearSelection();
            }
          },
          err => {
            this.toastr.error(
              this.translate.instant('DISTRIBUTION.cases.list.actions.assign_batch.result.error'));
            this.serverResponse = err.error;
          },
        ),
    );
  }

  public handleFiltersState($event: boolean): void {
    this.filtersReady = $event;
  }

  public handleSubmitFilters($event: any): void {
    // this.requestFilters = $event;

    this.requestFilters.statuses = $event.statuses;
    //this.requestFilters.end_date = $event.end_date;
    //this.requestFilters.start_date = $event.start_date;

    this.requestFilters.start_date = $event.start_date ? new Date($event.start_date).toLocaleDateString('en-CA') : null;
    this.requestFilters.end_date = $event.end_date ? new Date($event.end_date).toLocaleDateString('en-CA') : null;
    
    this.requestFilters.search = $event.search;

    this.requestFilters.wallets = $event.wallets;

    this.requestFilters.displayPending = $event.displayPending ? $event.displayPending : 0;

    this.requestFilters.displayOnHold = $event.displayOnHold ? $event.displayOnHold : 0;

    this.requestFilters.cases = $event.cases;
    
    this.resetPagination();
    this.resetSort();
    //this.fetchBatches();
  }

  public applyFilters() {
    this.resetPagination();
    this.resetSort();
    this.fetchBatches();
  }

}
